import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const friendlyMessage = (err) => {
  const msg = (err?.message || "").toLowerCase();
  if (msg.includes("403") || msg.includes("unauthorized") || msg.includes("signature"))
    return "Couldn’t reach the file due to a network issue. Please check your connection and try again.";
  if (msg.includes("network") || msg.includes("fetch"))
    return "Couldn’t reach the file due to a network issue. Please check your connection and try again.";
  if (msg.includes("range"))
    return "Your browser could not fetch the file. Please try again.";
  return "Something went wrong while loading the PDF.";
};

const PDFViewer = ({ pdfUrl }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const canvasRef = useRef();
  const renderTaskRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      const handler = e => e.preventDefault();
      node.addEventListener("contextmenu", handler);
      return () => node.removeEventListener("contextmenu", handler);
    }
  }, []);

  const loadPdf = () => {
    if (!pdfUrl) return;
    setError(null);
    setPdfDoc(null);
    setNumPages(null);
    setPageNum(1);
    setLoading(true);

    // If needed to reduce CORS friction:
    // const opts = { url: pdfUrl, disableRange: true, disableStream: true };
    const task = pdfjsLib.getDocument(pdfUrl);

    task.promise
      .then((pdf) => {
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
      })
      .catch((err) => {
        console.error("PDF load error:", err);
        setLoading(false);
        setError(friendlyMessage(err));
      });
  };

  // Load on mount/url change
  useEffect(() => {
    if (pdfUrl) loadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  // Render current page (with cancellation)
  useEffect(() => {
    if (!pdfDoc) return;

    let cancelled = false;
    setLoading(true);

    pdfDoc.getPage(pageNum)
      .then((page) => {
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.15 });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // Cancel previous render task if running
        renderTaskRef.current?.cancel?.();

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;

        renderTask.promise
          .then(() => { if (!cancelled) setLoading(false); })
          .catch((err) => {
            if (err?.name === "RenderingCancelledException") return;
            console.error("PDF render error:", err);
            setLoading(false);
            setError(friendlyMessage(err)); // <-- IMPORTANT
          });
      })
      .catch((err) => {
        console.error("PDF getPage error:", err);
        setLoading(false);
        setError(friendlyMessage(err));     // <-- IMPORTANT
      });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [pdfDoc, pageNum]);

  if (!pdfUrl) return <div>No PDF selected.</div>;

  return (
    <div style={{ textAlign: "center", minHeight: 600, background: "#fafafa" }}>
      <div>
        <button className="btn-primary text-white rounded-pill px-4" disabled={pageNum <= 1} onClick={() => setPageNum(pageNum - 1)}>
          Previous
        </button>
        <span style={{ margin: "0 12px" }}>
          Page {pageNum} / {numPages || "--"}
        </span>
        <button className="btn-primary text-white rounded-pill px-4" disabled={!numPages || pageNum >= numPages} onClick={() => setPageNum(pageNum + 1)}>
          Next
        </button>
      </div>
      <div ref={containerRef} style={{ display: "flex", justifyContent: "center", minHeight: 500, position: "relative" }}>
        {loading && (
          <div style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
            background: "rgba(255,255,255,0.85)", padding: 16, borderRadius: 8, zIndex: 2
          }}>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Loading page...
          </div>
        )}
        <canvas ref={canvasRef} style={{ boxShadow: "0 2px 8px #aaa", margin: "16px 0" }} />
      </div>
    </div>
  );
};

export default PDFViewer;
